import { css, useTheme } from "@emotion/react"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAnonymousAPI } from "../../../../services/api"
import { useSignUpSchemaProperties } from "../../../forms/validation-schemas/use-sign-up-validation-scheme"
import { FormProvider, useForm } from "react-hook-form"
import { SignUpFormType } from "../../../../models/sign-up-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useAuthModals } from "../../../../context/AuthModalsContext"
import { Flex } from "rebass"
import { focusNextElement } from "../../../forms/focusNextElement"
import { TextInput } from "../../../forms/inputs/TextInput"
import { translationKeys } from "../../../../util/translationKeys"
import { BodySubtextSemibold } from "../../../typography/text"
import { SelectInput } from "../../../forms/inputs/SelectInput"
import { returnDefaultPhonenumberPrefix } from "../../../../util/default-phonenumber-prefix"
import { countrySelectorOptions } from "../../../checkout/countrySelectorOptions"
import { CheckboxInput } from "../../../forms/inputs/CheckboxInput"
import { InlineSpan } from "../../../inline-span/InlineSpan"
import { ErrorBox } from "../../../errors/ErrorBox"
import { ButtonPrimary } from "../../../button/buttons"
import { Term } from "../../../../models/signup-info-item"
import { ErrorMessage } from "@hookform/error-message"

const termsWrapper = css`
  flex-direction: column;
  width: 100%;
  gap: 16px;
  margin-top: 8px !important;
  margin-bottom: 8px !important;
`

const phonenumberColumnStyle = css`
  flex-direction: column;
  width: 100%;
`

const phonenumberRowStyle = css`
  flex-direction: row;
  height: fit-content !important;
  gap: 8px;
  margin-top: 8px !important;
`

const formStyle = css`
  align-items: center;
  align-content: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: space-between;
  gap: 8px;
`

const bottomStyle = css`
  flex-direction: column;
  width: 100%;
  gap: 16px;
  padding-top: 8px;
`

const termContainerStyle = css`
  gap: 16px;
  display: flex;
  flex-direction: column;
`

type Props = {
  terms: Term[]
  setSignUpData: (data: SignUpFormType) => void
  setSignUpError: (value: string | undefined) => void
  signUpError: string | undefined
  countryCode?: string
}

export const SignUpForm = ({
  terms,
  setSignUpData,
  signUpError,
  setSignUpError,
  countryCode,
}: Props) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const anonymousAPI = useAnonymousAPI()

  const [isSendingCode, setIsSendingCode] = useState(false)

  const { visibleAuthModal, setVisibleAuthModal } = useAuthModals()

  const signUpSchemaProperties = useSignUpSchemaProperties()

  const methods = useForm<SignUpFormType>({
    mode: "onChange",
    resolver: yupResolver(signUpSchemaProperties),
    defaultValues: {
      time_zone: "Europe/Copenhagen",
      phone_number_prefix: returnDefaultPhonenumberPrefix(countryCode).value,
    },
    context: {
      terms,
    },
  })

  const { handleSubmit, setFocus, getValues, watch, reset } = methods

  const email = watch("email")

  useEffect(() => {
    setSignUpError(undefined)
  }, [email])

  // reset values if anything else than confirm code modal and signup modal opened
  useEffect(() => {
    if (
      visibleAuthModal === "confirm-code" ||
      visibleAuthModal === "register"
    ) {
      return
    }

    reset()
  }, [visibleAuthModal])

  const onSubmit = async (signupData: SignUpFormType) => {
    setIsSendingCode(true)
    try {
      await anonymousAPI.auth.requestEmailVerification({
        email: signupData.email,
      })
      setSignUpData(signupData)
      setVisibleAuthModal("confirm-code")
      setIsSendingCode(false)
    } catch (error) {
      setIsSendingCode(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form>
        <Flex
          // @ts-ignore
          onKeyUp={(e) => focusNextElement(e, getValues(), setFocus)}
          css={formStyle}
        >
          <TextInput
            label={`${t(translationKeys.comboCreateaccountFirst)}`}
            placeholder={`${t(translationKeys.comboSignupFirstNameHint)}`}
            name="first_name"
            required={true}
          />
          <TextInput
            label={`${t(translationKeys.comboCreateaccountLast)}`}
            placeholder={`${t(translationKeys.comboSignupLastNameHint)}`}
            name="last_name"
            required={true}
          />
          <Flex css={phonenumberColumnStyle}>
            <BodySubtextSemibold mt={2} color={theme.colors.primary}>
              {t(translationKeys.comboCreateaccountPhone)}
            </BodySubtextSemibold>
            <Flex css={phonenumberRowStyle}>
              <SelectInput
                name="phone_number_prefix"
                options={countrySelectorOptions}
                onChange={() => {
                  methods.trigger("phone_number")
                }}
              />
              <TextInput
                placeholder={`${t(translationKeys.comboSignupPhoneHint)}`}
                name="phone_number"
                required={true}
              />
            </Flex>
          </Flex>
          <TextInput
            label={`${t(translationKeys.comboCreateaccountEmail)}`}
            placeholder={`${t(translationKeys.comboSignupEmailHint)}`}
            type="email"
            autoComplete="new-password"
            name="email"
            required={true}
          />
          <TextInput
            label={`${t(translationKeys.comboCreateaccountPassword)}`}
            placeholder={`${t(translationKeys.comboLoginPasswordHint)}`}
            type="password"
            autoComplete="new-password"
            name="password"
            required={true}
          />
          <Flex css={termsWrapper}>
            {terms?.map((term, index) => {
              return (
                <div css={termContainerStyle} key={index}>
                  <CheckboxInput
                    key={index}
                    name={`accepted_terms.${index}`}
                    label={term.disclaimer}
                    value={term.id}
                    labelLink={
                      <InlineSpan
                        onClick={() => {
                          window?.open(term._links.terms_html)
                        }}
                      >
                        {t(translationKeys.webSeeTerms)}
                      </InlineSpan>
                    }
                  />
                </div>
              )
            })}
            {terms?.map((_, index) => {
              return (
                <ErrorMessage
                  key={index}
                  name={`accepted_terms.${index}`}
                  render={({ message }) => <ErrorBox title={message} />}
                />
              )
            })}
          </Flex>
          {signUpError && <ErrorBox title={signUpError} />}
          <Flex css={bottomStyle}>
            <ButtonPrimary
              type="button"
              backgroundColor={theme.colors.ctaMain}
              hoverColor={theme.colors.ctaSecondaryOne}
              color={theme.colors.white}
              loading={isSendingCode}
              onClick={handleSubmit(onSubmit)}
            >
              {t(translationKeys.comboCreateaccountGetStarted)}
            </ButtonPrimary>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  )
}
